import type {
  ReportListResponse,
  ReportDetail,
  ReportPublishResponse,
  SaveReportDraftPayload,
} from "../types/report";
import { apiFetch } from "./client";

const BASE = "/api/v1/doctor/reports";

export async function listReports(
  caseId?: string,
  page = 1,
  perPage = 20,
): Promise<ReportListResponse> {
  const qs = new URLSearchParams();
  if (caseId) qs.set("case_id", caseId);
  qs.set("page", String(page));
  qs.set("per_page", String(perPage));
  return apiFetch<ReportListResponse>(`${BASE}?${qs.toString()}`);
}

export async function getReport(id: string): Promise<ReportDetail> {
  return apiFetch<ReportDetail>(`${BASE}/${id}`);
}

export async function saveReportDraft(id: string, payload: SaveReportDraftPayload): Promise<unknown> {
  return apiFetch(`${BASE}/${id}/draft`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export async function publishReport(id: string, attestationChecked: boolean): Promise<ReportPublishResponse> {
  return apiFetch<ReportPublishResponse>(`${BASE}/${id}/publish`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ attestation_checked: attestationChecked }),
  });
}

export async function getLabPdfUrl(id: string): Promise<string> {
  const data = await apiFetch<{ url: string }>(`${BASE}/${id}/lab-pdf`);
  return data.url;
}

/**
 * Result of fetching the doctor-copy published PDF (HH-262).
 *
 * Discriminated union mirrors the backend's non-throwing contract: the
 * caller never has to catch 404 for routine "PDF not ready" cases. The
 * 'unavailable' variant covers both legacy rows (pre-HH-262) and
 * not-yet-published reports — the UI hides the button in both cases.
 */
export type PublishedPdfResult =
  | { kind: "url"; url: string }
  | { kind: "generating" }
  | { kind: "unavailable" };

interface PublishedPdfBody {
  url?: string;
  error?: string;
}

export async function getPublishedPdfUrl(id: string): Promise<PublishedPdfResult> {
  // Bypass apiFetch's throw-on-non-2xx behavior: we need to handle the
  // 404 (legacy / not published / not found) and 202 (still generating)
  // branches without surfacing a red error toast.
  let body: PublishedPdfBody;
  try {
    body = await apiFetch<PublishedPdfBody>(`${BASE}/${id}/published-pdf`);
  } catch (err) {
    // apiFetch throws for any non-2xx. 404 means "no PDF available" —
    // collapse to 'unavailable'. Network / 5xx still bubbles up.
    const msg = err instanceof Error ? err.message : String(err);
    if (msg.toLowerCase().includes("not found") || msg.includes("404")) {
      return { kind: "unavailable" };
    }
    throw err;
  }

  if (body.url) return { kind: "url", url: body.url };
  if (body.error === "pdf_generating") return { kind: "generating" };
  return { kind: "unavailable" };
}
